import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { FolderOpen, Download } from "lucide-react";

export default function Resources() {
    const [items, setItems] = useState([]);
    const [activeCat, setActiveCat] = useState("All");
    const [open, setOpen] = useState(null);

    useEffect(() => {
        api.get("/resources").then((r) => setItems(r.data));
    }, []);

    const cats = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category).filter(Boolean)))], [items]);
    const filtered = activeCat === "All" ? items : items.filter((i) => i.category === activeCat);

    return (
        <div className="space-y-10" data-testid="resources-page">
            <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-ink-400 font-mono mb-3">Library</div>
                <h1 className="font-editorial text-4xl md:text-5xl text-ink-900 leading-[1.05]">Resources.</h1>
                <p className="mt-3 text-base text-ink-500 max-w-xl">Scripts, cheat sheets, venue guides, battle cards — the operational reference shelf.</p>
            </div>

            <div className="flex flex-wrap gap-2">
                {cats.map((c) => (
                    <button
                        key={c}
                        onClick={() => setActiveCat(c)}
                        data-testid={`resource-cat-${c}`}
                        className={`px-3 py-1.5 text-xs font-mono uppercase tracking-[0.15em] rounded-sm border transition-colors ${
                            activeCat === c ? "bg-haevn-700 text-white border-haevn-700" : "bg-white text-ink-500 border-ink-300 hover:border-haevn-700 hover:text-ink-900"
                        }`}
                    >{c}</button>
                ))}
            </div>

            {items.length === 0 ? (
                <EmptyState title="No resources yet" description="Your administrator can upload scripts and PDFs from the Admin panel." icon={FolderOpen} testId="resources-empty" />
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-ink-300 border border-ink-300">
                    {filtered.map((r) => (
                        <button
                            key={r.id}
                            onClick={() => setOpen(r)}
                            data-testid={`resource-${r.id}`}
                            className="bg-white p-6 text-left hover:bg-ink-100/60 transition-colors group"
                        >
                            <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">{r.category}</div>
                            <div className="mt-2 font-heading text-lg text-ink-900 group-hover:text-haevn-700 transition-colors">{r.title}</div>
                            {r.description && <div className="mt-1 text-sm text-ink-500 line-clamp-3">{r.description}</div>}
                            {r.file_url && (
                                <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-haevn-700">
                                    <Download size={12} /> Download
                                </div>
                            )}
                        </button>
                    ))}
                </div>
            )}

            {open && (
                <div className="fixed inset-0 z-40 bg-ink-900/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setOpen(null)}>
                    <div className="bg-white border border-ink-300 max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8 md:p-10" onClick={(e) => e.stopPropagation()} data-testid="resource-modal">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">{open.category}</div>
                        <h2 className="font-editorial text-3xl mt-2">{open.title}</h2>
                        {open.description && <p className="text-sm text-ink-500 mt-2">{open.description}</p>}
                        {open.body && <div className="mt-6 text-base leading-relaxed whitespace-pre-line text-ink-700">{open.body}</div>}
                        {open.file_url && (
                            <a href={open.file_url} target="_blank" rel="noopener noreferrer" data-testid="resource-download-link" className="mt-6 inline-flex items-center gap-2 bg-haevn-700 text-white px-5 py-2 rounded-sm text-sm hover:bg-haevn-900 transition-colors">
                                <Download size={14} /> Open file
                            </a>
                        )}
                        <button onClick={() => setOpen(null)} className="mt-6 ml-3 border border-ink-300 px-5 py-2 rounded-sm text-sm hover:border-haevn-700">Close</button>
                    </div>
                </div>
            )}
        </div>
    );
}
